import { getPageToRouterMap } from './getPageToRouterMap'
import { getPatternDefForPage } from '@wix/wix-data-client-common/dist/esm/business-logic/dynamic-pages/router-config/routerConfig'
import { get } from 'lodash'

const groupTitle = (name, prefix, t, isStudioEditor) => {
  const collectionName = prefix ? `${prefix}/${name}` : name

  if (isStudioEditor) {
    return t('App_Pages_Group_Title_Studio', {
      collectionName,
    })
  }

  return t('App_Pages_Group_Title', {
    name: collectionName,
  })
}

const getGroupOrder = ({ displayName, displayNamespace, namespace, id }) =>
  displayNamespace || namespace ? 2 : displayName ? 1 : id ? 3 : 4

const getGroupTitle = (
  { displayName, displayNamespace, namespace, id },
  t,
  isStudioEditor,
) =>
  (displayName &&
    groupTitle(
      displayName,
      displayNamespace || namespace,
      t,
      isStudioEditor,
    )) ||
  (id && t('App_Pages_Removed_Collection', { name: id })) ||
  t('App_Pages_Broken_Group_Title')

const createCollectionGroup = (
  collectionId,
  existingCollection,
  t,
  isStudioEditor,
  isPermittedToManageItems,
) => {
  const collection = existingCollection || { id: collectionId }

  const groupTitle = getGroupTitle(collection, t, isStudioEditor)

  const groupActions = [
    {
      title: t('App_Pages_Actions_Manage_Collection'),
      event: 'openContentManager',
      icon: 'manage_content',
      tooltip: t('App_Pages_Actions_Manage_Collection_Tooltip', {
        title: groupTitle,
      }),
    },
  ]

  const studioGroupActions = [
    ...[
      isPermittedToManageItems
        ? {
            title: t('App_Pages_Actions_Manage_Single_Collection'),
            event: 'manageCollectionClicked',
          }
        : null,
    ].filter(Boolean),
    {
      title: t('Collections_Tree_Context_Menu_Actions_Add_Dynamic_Page'),
      event: 'openNewDynamicPagePanel',
    },
  ]

  return {
    id: collectionId,
    title: groupTitle,
    order: getGroupOrder(collection),
    pages: [],
    groupActions:
      existingCollection &&
      (isStudioEditor ? studioGroupActions : groupActions),
  }
}

const collectionPageMapper =
  ({ collectionsById, t, isStudioEditor, isPermittedToManageItems }) =>
  (collectionGroups, [{ title, pageRef }, { config, pages }]) => {
    const patternDef = getPatternDefForPage(config, pages, pageRef)
    const collectionId = get(patternDef, 'def.config.collection', null)

    const collectionGroup =
      collectionGroups[collectionId] ||
      (collectionGroups[collectionId] = createCollectionGroup(
        collectionId,
        collectionsById[collectionId],
        t,
        isStudioEditor,
        isPermittedToManageItems,
      ))

    collectionGroup.pages.push({ title, id: pageRef.id })

    return collectionGroups
  }

const mapGroup = ([, { id, title, pages, groupActions }]) => ({
  id,
  title,
  groupActions,
  pages: pages
    .sort(({ title }, { title: compareTitle }) =>
      title.localeCompare(compareTitle),
    )
    .map(({ id }) => id),
})

const createDynamicPageGroups = async ({
  routers,
  collections,
  t,
  editorSdkProxy,
  isStudioEditor,
  isPermittedToManageItems,
}) => {
  const collectionsById = collections.reduce((acc, collection) => {
    acc[collection.id] = collection

    return acc
  }, {})

  const collectionGroups = Array.from(
    await getPageToRouterMap({
      editorSdkProxy,
      routers,
    }),
  ).reduce(
    collectionPageMapper({
      collectionsById,
      t,
      isStudioEditor,
      isPermittedToManageItems,
    }),
    {},
  )

  return Object.entries(collectionGroups)
    .sort(
      (
        [, { title, order }],
        [, { title: compareTitle, order: compareOrder }],
      ) =>
        order === compareOrder
          ? title.localeCompare(compareTitle)
          : order - compareOrder,
    )
    .map(mapGroup)
}

export default createDynamicPageGroups
